import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle } from "lucide-react";

import type { AdminOrganization } from "@/api/client";

/**
 * THE DELETE CONFIRMATION.
 *
 * Not a toast, and not an "Are you sure?" with an OK button — because both of those are
 * dismissed by muscle memory, and this one is not undoable. Deleting an organization
 * destroys the tenant and every row that belongs to it: sites, tanks, generators, and the
 * entire fuel ledger a customer may be legally required to keep.
 *
 * ── THE FOUR LOCKS, AND WHERE EACH ONE LIVES ────────────────────────────────
 *
 *  1. THE ORGANIZATION MUST ALREADY BE SUSPENDED.  In the DOMAIN
 *     (organization.Destroy). The server answers 409 otherwise. A UI cannot skip it,
 *     because the UI is not where it lives — which is the point.
 *  2. THE NAME MUST BE TYPED OUT.  Here. It is the one lock that requires the human to
 *     have READ what is in front of them: you cannot type "Rafidain Bank" by reflex.
 *  3. A REASON IS MANDATORY.  Here, and enforced again in the use case. It goes into the
 *     append-only audit log, and it is what makes the act defensible six months later.
 *  4. THE AUDIT TRAIL RECORDS WHAT WAS DESTROYED.  In the use case: the row counts, read
 *     before the cascade, because afterwards there is nothing left to count.
 *
 * This component owns exactly lock 2 and half of lock 3. It is the last one, not the only
 * one, and it is deliberately not trusted to be.
 */
export function DeleteOrganizationDialog({
  org,
  open,
  onOpenChange,
  onConfirm,
  pending,
  error,
}: {
  org: AdminOrganization;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  pending: boolean;
  error?: string;
}) {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");

  const suspended = org.status === "suspended";
  const nameMatches = typed.trim() === org.name;
  const reasonGiven = reason.trim().length >= 8;
  const armed = suspended && nameMatches && reasonGiven && !pending;

  const usage = org.usage;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setTyped("");
          setReason("");
        }
        onOpenChange(next);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/70" />
        <Dialog.Content
          className="panel fixed left-1/2 top-1/2 z-50 w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 p-6"
          aria-describedby="delete-org-description"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle aria-hidden className="mt-0.5 size-6 shrink-0 text-[var(--color-dry)]" />
            <div>
              <Dialog.Title className="text-lg font-semibold text-[var(--color-text)]">
                Delete {org.name}?
              </Dialog.Title>
              <Dialog.Description
                id="delete-org-description"
                className="mt-1 text-sm text-[var(--color-muted)]"
              >
                This is irreversible. Everything this customer has recorded is destroyed.
              </Dialog.Description>
            </div>
          </div>

          {/* WHAT WILL BE DESTROYED. Named, counted, before the fact — not a vague warning. */}
          <ul className="tabular mt-4 grid grid-cols-2 gap-x-6 gap-y-1 rounded border border-[var(--color-rule)] bg-[var(--color-ink)]/40 p-3 text-sm">
            <li className="flex justify-between">
              <span className="text-[var(--color-muted)]">Members</span>
              <span>{usage.members}</span>
            </li>
            <li className="flex justify-between">
              <span className="text-[var(--color-muted)]">Sites</span>
              <span>{usage.sites}</span>
            </li>
            <li className="flex justify-between">
              <span className="text-[var(--color-muted)]">Tanks</span>
              <span>{usage.tanks}</span>
            </li>
            <li className="flex justify-between">
              <span className="text-[var(--color-muted)]">Generators</span>
              <span>{usage.generators}</span>
            </li>
          </ul>

          {!suspended && (
            <p
              role="status"
              className="mt-4 rounded border border-[var(--color-warn)] bg-[var(--color-warn-wash)] p-3 text-sm text-[var(--color-text)]"
            >
              <strong className="font-semibold">Suspend it first.</strong> An active organization
              cannot be deleted — the server refuses it. Suspension is reversible; this is not.
            </p>
          )}

          <label className="mt-4 block text-sm">
            <span className="text-[var(--color-muted)]">
              Type <strong className="font-mono text-[var(--color-text)]">{org.name}</strong> to confirm
            </span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={!suspended}
              autoComplete="off"
              aria-label={`Type ${org.name} to confirm`}
              className="mt-1 w-full rounded border border-[var(--color-rule-strong)] bg-[var(--color-ink)] px-3 py-2 font-mono text-[var(--color-text)] disabled:opacity-50"
            />
          </label>

          <label className="mt-3 block text-sm">
            <span className="text-[var(--color-muted)]">Reason (goes to the audit log, forever)</span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={!suspended}
              placeholder="Customer requested erasure under clause 9.2, TKT-4471"
              aria-label="Reason"
              className="mt-1 w-full rounded border border-[var(--color-rule-strong)] bg-[var(--color-ink)] px-3 py-2 text-[var(--color-text)] disabled:opacity-50"
            />
          </label>

          {error && (
            <p role="alert" className="mt-3 text-sm text-[var(--color-dry)]">
              {error}
            </p>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded border border-[var(--color-rule-strong)] px-4 py-2 text-sm text-[var(--color-text)] hover:bg-[var(--color-panel-high)]"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              disabled={!armed}
              // The accessible name does NOT change while the request is in flight. A
              // control whose name flips under a user is a control a screen-reader user
              // loses track of mid-action — and this is not the action to lose track of.
              aria-label="Delete permanently"
              onClick={() => onConfirm(reason.trim())}
              className="rounded bg-[var(--color-dry-deep)] px-4 py-2 text-sm font-semibold text-[var(--color-cream)] hover:bg-[var(--color-dry)] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {pending ? "Deleting…" : "Delete permanently"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
